import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import HumanHoursEditor from "@/components/HumanHoursEditor";
import { TIMEZONE_OPTIONS, setTenantTz } from "@/lib/tz";
import EfiBoletoSettings from "@/components/EfiBoletoSettings";
import QuickRepliesEditor from "@/components/QuickRepliesEditor";
import { Save, Wifi, Database, KeyRound, FileKey, Webhook, FileText, Split, Sparkles, Copy, Code2, ChevronDown } from "lucide-react";

// Gera uma chave forte, aleatoria de verdade (crypto do navegador, nao
// Math.random) - com um prefixo legivel pra facilitar reconhecer qual
// integracao e qual so de bater o olho, tanto aqui quanto no arquivo PHP.
function generateApiKey(prefix) {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${prefix}-${hex}`;
}

function copyToClipboard(text, label) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(label || "Copiado!"),
    () => toast.error("Não consegui copiar - selecione o texto manualmente"),
  );
}

// Card de "codigo pronto pra colar no mk-auth" - mostra so a linha que
// precisa mudar no arquivo PHP, com instrucao clara de onde encontrar,
// e um botao de copiar. Evita ter que colar o arquivo PHP inteiro (mais
// de 300 linhas em cada um) so pra trocar uma linha.
function PhpKeySnippet({ fileName, apiKey }) {
  const [open, setOpen] = useState(false);
  const line = `$API_KEY = '${apiKey || "sua-chave-aqui"}';`;
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/50">
      <button
        type="button"
        data-testid={`snippet-toggle-${fileName}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-400 hover:text-emerald-300"
      >
        <span className="flex items-center gap-1.5">
          <Code2 className="w-3.5 h-3.5" /> Código para colar no mk-auth ({fileName})
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-[11px] text-slate-500 leading-relaxed">
            No seu servidor mk-auth, abra o arquivo <span className="font-mono-jb text-slate-300">chatbot-api/{fileName}</span>,
            localize a linha que começa com <span className="font-mono-jb text-slate-300">$API_KEY =</span> perto do topo do
            arquivo, e substitua pela linha abaixo. Depois salve — não precisa reiniciar nada.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono-jb text-emerald-300 bg-slate-900 border border-slate-800 rounded px-3 py-2 overflow-x-auto whitespace-nowrap">
              {line}
            </code>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid={`snippet-copy-${fileName}`}
              onClick={() => copyToClipboard(line, "Linha copiada - cole no arquivo PHP")}
              className="border-slate-700 hover:bg-slate-800 hover:text-white shrink-0"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [efiTesting, setEfiTesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [certPass, setCertPass] = useState("");
  const fileRef = useRef();

  const [openpix, setOpenpix] = useState({ app_id: "", base_url: "https://api.openpix.com.br", configured: false });
  const [split, setSplit] = useState({
    enabled: false, mode: "percentage", efi_percent: 50, target_provider: "efi", target_value_cents: 0,
    efi_configured: false, openpix_configured: false, month_totals_cents: { efi: 0, openpix: 0 },
  });
  const [savingOpenpix, setSavingOpenpix] = useState(false);
  const [savingSplit, setSavingSplit] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: op }, { data: sp }] = await Promise.all([
        api.get("/tenant/settings"),
        api.get("/tenant/openpix"),
        api.get("/tenant/payment-split"),
      ]);
      setData(data);
      setOpenpix(op);
      setSplit(sp);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setMk = (k) => (e) =>
    setData((d) => ({ ...d, mkauth: { ...(d.mkauth || {}), [k]: e.target.value } }));
  const setMkApi = (k) => (e) =>
    setData((d) => ({ ...d, mkauth_api: { ...(d.mkauth_api || {}), [k]: e.target.value } }));
  const setMkRest = (k) => (e) =>
    setData((d) => ({ ...d, mkauth_rest: { ...(d.mkauth_rest || {}), [k]: e.target.value } }));
  const setMkJuros = (k) => (e) =>
    setData((d) => ({ ...d, mkauth_juros: { ...(d.mkauth_juros || {}), [k]: e.target.value } }));
  const setEvo = (k) => (e) =>
    setData((d) => ({ ...d, evolution: { ...(d.evolution || {}), [k]: e.target.value } }));
  const setEfi = (k) => (e) =>
    setData((d) => ({ ...d, efi: { ...(d.efi || {}), [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: data.name,
        mkauth: {
          host: data.mkauth?.host || "",
          port: parseInt(data.mkauth?.port || 3306, 10),
          user: data.mkauth?.user || "",
          password: data.mkauth?.password || "",
          database: data.mkauth?.database || "mkradius",
        },
        mkauth_api: {
          base_url: data.mkauth_api?.base_url || "",
          api_key: data.mkauth_api?.api_key || "",
        },
        mkauth_rest: {
          tunnel: data.mkauth_rest?.tunnel || "",
          client_id: data.mkauth_rest?.client_id || "",
          client_secret: data.mkauth_rest?.client_secret || "",
        },
        mkauth_juros: {
          base_url: data.mkauth_juros?.base_url || "",
          api_key: data.mkauth_juros?.api_key || "",
          timeout: parseFloat(data.mkauth_juros?.timeout || 5),
          retries: parseInt(data.mkauth_juros?.retries || 3, 10),
        },
        evolution: {
          base_url: data.evolution?.base_url || "",
          api_key: data.evolution?.api_key || "",
          instance_name: data.evolution?.instance_name || "",
        },
        efi: {
          environment: data.efi?.environment || "sandbox",
          client_id: data.efi?.client_id || "",
          client_secret: data.efi?.client_secret || "",
          pix_key: data.efi?.pix_key || "",
          webhook_secret: data.efi?.webhook_secret || "",
        },
        scheduler_enabled: !!data.scheduler_enabled,
        scheduler_hour: Math.min(23, Math.max(0, parseInt(data.scheduler_hour ?? 9, 10) || 0)),
        scheduler_minute: Math.min(59, Math.max(0, parseInt(data.scheduler_minute ?? 0, 10) || 0)),
        timezone: data.timezone || "America/Recife",
        bot_paused: !!data.bot_paused,
        handoff_pause_minutes: Math.min(1440, Math.max(1, parseInt(data.handoff_pause_minutes ?? 10, 10) || 10)),
        payment_confirm_image_url: data.payment_confirm_image_url || "",
        bypass_numbers: parseBypassNumbers(data.bypass_numbers_text ?? (data.bypass_numbers || []).join("\n")),
        loop_guard_enabled: data.loop_guard_enabled !== false,
        ...(data.human_hours ? { human_hours: data.human_hours } : {}),
        call_reply_enabled: data.call_reply_enabled !== false,
        block_notice_enabled: data.block_notice_enabled !== false,
        call_reply_text: data.call_reply_text || "",
        quick_replies: (data.quick_replies || []).filter((q) => q.label?.trim() && q.text?.trim()),
        avulsa_multa_pct: Number(data.avulsa_multa_pct) || 0,
        avulsa_juros_pct: Number(data.avulsa_juros_pct) || 0,
      };
      await api.put("/tenant/settings", payload);
      toast.success("Configurações salvas");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const testMk = async () => {
    setTesting(true);
    try {
      const { data: res } = await api.post("/tenant/mkauth/test");
      if (res.ok) toast.success(res.message);
      else toast.error(res.message);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setTesting(false);
    }
  };

  const testMkApi = async () => {
    try {
      const { data: res } = await api.post("/tenant/mkauth-api/test");
      if (res.ok) toast.success(`API PDF OK - HTTP ${res.status}`);
      else toast.error(res.error || `HTTP ${res.status}`);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const testMkRest = async () => {
    try {
      const { data: res } = await api.post("/tenant/mkauth-rest/test");
      if (res.ok) toast.success("API REST do mk-auth OK - autenticou");
      else toast.error(res.error || "Falha ao autenticar");
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const testMkJuros = async () => {
    try {
      const { data: res } = await api.post("/tenant/mkauth-juros/test");
      if (res.ok) toast.success(`API Cálculo PIX OK - HTTP ${res.status}`);
      else toast.error(res.error || `HTTP ${res.status}`);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const testEfi = async () => {
    setEfiTesting(true);
    try {
      const { data: res } = await api.post("/tenant/efi/test");
      if (res.ok) toast.success(`EFI OK (${res.environment})`);
      else toast.error(res.message);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setEfiTesting(false);
    }
  };

  const saveOpenpix = async () => {
    setSavingOpenpix(true);
    try {
      await api.put("/tenant/openpix", { app_id: openpix.app_id, base_url: openpix.base_url });
      toast.success("OpenPix salva");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSavingOpenpix(false);
    }
  };

  const registerOpenpixWebhook = async () => {
    try {
      const { data: res } = await api.post("/tenant/openpix/webhook/register");
      if (res.ok) toast.success(res.already_exists ? "Webhook já estava registrado" : "Webhook registrado na OpenPix");
      else toast.error("Não registrou. Verifique o AppID e tente novamente.");
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const saveSplit = async () => {
    setSavingSplit(true);
    try {
      const body = {
        enabled: split.enabled,
        mode: split.mode,
        efi_percent: parseInt(split.efi_percent, 10) || 50,
        target_provider: split.target_provider,
        target_value_cents: Math.round(parseFloat(String(split.target_value_raw ?? (split.target_value_cents / 100)).replace(",", ".")) * 100) || 0,
      };
      await api.put("/tenant/payment-split", body);
      toast.success("Divisão de recebimento salva");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSavingSplit(false);
    }
  };

  const [webhookInfo, setWebhookInfo] = useState(null);
  const [webhookChecking, setWebhookChecking] = useState(false);
  const checkWebhook = async () => {
    setWebhookChecking(true);
    try {
      const { data: res } = await api.get("/tenant/efi/webhook");
      setWebhookInfo(res);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setWebhookChecking(false);
    }
  };

  const registerWebhook = async () => {
    try {
      const { data: res } = await api.post("/tenant/efi/webhook/register");
      if (res.ok) toast.success(`Webhook registrado: ${res.url}`);
      else toast.error(res.error || "Falha ao registrar webhook");
      checkWebhook();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const uploadCert = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Selecione um arquivo .p12");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("password", certPass || "");
      await api.post("/tenant/efi/certificate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Certificado carregado com sucesso");
      setCertPass("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setUploading(false);
    }
  };

  if (!data) return null;

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Empresa</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">
          Configurações
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Todas as credenciais e endpoints ficam isolados por empresa (tenant).
        </p>
      </header>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <Database className="w-4 h-4 text-emerald-400" />
          <div className="font-outfit text-white font-semibold">mk-auth (MySQL)</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Host" testid="mk-host">
            <Input value={data.mkauth?.host || ""} onChange={setMk("host")} placeholder="10.0.0.1" className={inputCls} />
          </Field>
          <Field label="Porta" testid="mk-port">
            <Input value={data.mkauth?.port || 3306} onChange={setMk("port")} className={inputCls} />
          </Field>
          <Field label="Usuário" testid="mk-user">
            <Input value={data.mkauth?.user || ""} onChange={setMk("user")} className={inputCls} />
          </Field>
          <Field label="Senha" testid="mk-pass">
            <Input
              type="password"
              value={data.mkauth?.password || ""}
              onChange={setMk("password")}
              placeholder="••••••••"
              className={inputCls}
            />
          </Field>
          <Field label="Database" testid="mk-db">
            <Input value={data.mkauth?.database || ""} onChange={setMk("database")} placeholder="mkradius" className={inputCls} />
          </Field>
        </div>
        <Button
          data-testid="mk-test-btn"
          onClick={testMk}
          disabled={testing}
          variant="outline"
          className="border-slate-700 hover:bg-slate-800 hover:text-white"
        >
          <Wifi className="w-4 h-4 mr-2" /> Testar Conexão
        </Button>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <FileText className="w-4 h-4 text-emerald-400" />
          <div className="font-outfit text-white font-semibold">API PDF do mk-auth (envia boleto oficial)</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="URL da API" testid="mkapi-url">
            <Input
              value={data.mkauth_api?.base_url || ""}
              onChange={setMkApi("base_url")}
              placeholder="http://ip-do-mkauth  (ou URL completa terminando em .php)"
              className={inputCls}
            />
          </Field>
          <Field label="X-API-Key" testid="mkapi-key">
            <div className="flex gap-2">
              <Input
                type="password"
                value={data.mkauth_api?.api_key || ""}
                onChange={setMkApi("api_key")}
                placeholder="••••••••"
                className={inputCls}
              />
              <Button
                type="button"
                variant="outline"
                data-testid="mkapi-generate-btn"
                title="Gerar nova chave segura"
                onClick={() => {
                  setData((d) => ({ ...d, mkauth_api: { ...(d.mkauth_api || {}), api_key: generateApiKey("MKCHAT-pdf") } }));
                  toast.success("Chave gerada! Copie o código abaixo pro mk-auth e clique em Salvar aqui embaixo.");
                }}
                className="border-slate-700 hover:bg-slate-800 hover:text-emerald-300 shrink-0"
              >
                <Sparkles className="w-4 h-4" />
              </Button>
            </div>
          </Field>
        </div>
        <div className="text-xs text-slate-500">
          Endpoint chamado:{" "}
          <span className="font-mono-jb text-emerald-300">
            POST {`{URL}/chatbot-api/gerar_boleto_pdf.php`}
          </span>
          . Depois de enviar o PDF ao cliente, o arquivo é removido do servidor automaticamente.
        </div>
        <PhpKeySnippet fileName="gerar_boleto_pdf.php" apiKey={data.mkauth_api?.api_key} />
        <Button
          data-testid="mkapi-test-btn"
          onClick={testMkApi}
          variant="outline"
          className="border-slate-700 hover:bg-slate-800 hover:text-white"
        >
          <Wifi className="w-4 h-4 mr-2" /> Testar API PDF
        </Button>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <FileText className="w-4 h-4 text-emerald-400" />
          <div className="font-outfit text-white font-semibold">API REST do mk-auth (bloqueio/desbloqueio de avulsas)</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Túnel (URL)" testid="mkrest-tunnel">
            <Input
              value={data.mkauth_rest?.tunnel || ""}
              onChange={setMkRest("tunnel")}
              placeholder="https://seu-tunel.exemplo.com"
              className={inputCls}
            />
          </Field>
          <Field label="Client ID" testid="mkrest-client-id">
            <Input
              value={data.mkauth_rest?.client_id || ""}
              onChange={setMkRest("client_id")}
              className={inputCls}
            />
          </Field>
          <Field label="Client Secret" testid="mkrest-client-secret">
            <Input
              type="password"
              value={data.mkauth_rest?.client_secret || ""}
              onChange={setMkRest("client_secret")}
              placeholder="••••••••"
              className={inputCls}
            />
          </Field>
        </div>
        <div className="text-xs text-slate-500">
          Usada só para bloquear e desbloquear clientes de cobrança avulsa. Sem estes dados, o Quitaí grava o bloqueio direto no banco do mk-auth.
        </div>
        <Button
          data-testid="mkrest-test-btn"
          onClick={testMkRest}
          variant="outline"
          className="border-slate-700 hover:bg-slate-800 hover:text-white"
        >
          <Wifi className="w-4 h-4 mr-2" /> Testar API REST
        </Button>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <FileText className="w-4 h-4 text-emerald-400" />
          <div className="font-outfit text-white font-semibold">API Cálculo PIX do mk-auth (multa/juros/desconto)</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="URL da API" testid="mkjuros-url">
            <Input
              value={data.mkauth_juros?.base_url || ""}
              onChange={setMkJuros("base_url")}
              placeholder="https://central.seu-provedor.com.br  (ou URL completa terminando em .php)"
              className={inputCls}
            />
          </Field>
          <Field label="X-API-Key" testid="mkjuros-key">
            <div className="flex gap-2">
              <Input
                type="password"
                value={data.mkauth_juros?.api_key || ""}
                onChange={setMkJuros("api_key")}
                placeholder="MKCHAT-••••••••"
                className={inputCls}
              />
              <Button
                type="button"
                variant="outline"
                data-testid="mkjuros-generate-btn"
                title="Gerar nova chave segura"
                onClick={() => {
                  setData((d) => ({ ...d, mkauth_juros: { ...(d.mkauth_juros || {}), api_key: generateApiKey("MKCHAT-calcular-juros") } }));
                  toast.success("Chave gerada! Copie o código abaixo pro mk-auth e clique em Salvar aqui embaixo.");
                }}
                className="border-slate-700 hover:bg-slate-800 hover:text-emerald-300 shrink-0"
              >
                <Sparkles className="w-4 h-4" />
              </Button>
            </div>
          </Field>
          <Field label="Timeout (segundos)" testid="mkjuros-timeout">
            <Input
              type="number"
              min="1"
              step="1"
              value={data.mkauth_juros?.timeout ?? 5}
              onChange={setMkJuros("timeout")}
              className={inputCls}
            />
          </Field>
          <Field label="Tentativas (retries)" testid="mkjuros-retries">
            <Input
              type="number"
              min="1"
              max="10"
              step="1"
              value={data.mkauth_juros?.retries ?? 3}
              onChange={setMkJuros("retries")}
              className={inputCls}
            />
          </Field>
        </div>
        <div className="text-xs text-slate-500">
          Endpoint chamado:{" "}
          <span className="font-mono-jb text-emerald-300">
            POST {`{URL}/chatbot-api/calcular_juros.php`}
          </span>
          . <strong>Obrigatório para gerar PIX</strong>. Se essa API falhar, o chatbot NÃO gera cobrança e avisa o cliente para tentar novamente.
        </div>
        <PhpKeySnippet fileName="calcular_juros.php" apiKey={data.mkauth_juros?.api_key} />
        <Button
          data-testid="mkjuros-test-btn"
          onClick={testMkJuros}
          variant="outline"
          className="border-slate-700 hover:bg-slate-800 hover:text-white"
        >
          <Wifi className="w-4 h-4 mr-2" /> Testar API Cálculo PIX
        </Button>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <Wifi className="w-4 h-4 text-emerald-400" />
          <div className="font-outfit text-white font-semibold">Evolution API 2.3.7</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Base URL" testid="evo-url">
            <Input
              value={data.evolution?.base_url || ""}
              onChange={setEvo("base_url")}
              placeholder="http://evolution-api:8080  (rede evolution-api_evolution-net)"
              className={inputCls}
            />
          </Field>
          <Field label="Instance Name" testid="evo-instance">
            <Input
              value={data.evolution?.instance_name || ""}
              onChange={setEvo("instance_name")}
              placeholder="cobranca"
              className={inputCls}
            />
          </Field>
          <Field label="API Key" testid="evo-key">
            <Input
              value={data.evolution?.api_key || ""}
              onChange={setEvo("api_key")}
              placeholder="chave global ou de instância"
              className={inputCls}
            />
          </Field>
        </div>
        <div className="text-xs text-slate-500">
          O webhook do bot fica em{" "}
          <span className="font-mono-jb text-emerald-300">
            {`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook/${data.id}`}
          </span>
        </div>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <KeyRound className="w-4 h-4 text-emerald-400" />
          <div className="font-outfit text-white font-semibold">EFI (Gerencianet) PIX</div>
          <div className="text-xs text-slate-500 ml-auto">
            Cobranças PIX imediatas com valor do boleto do mk-auth
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Ambiente" testid="efi-env">
            <Select
              value={data.efi?.environment || "sandbox"}
              onValueChange={(v) => setData((d) => ({ ...d, efi: { ...(d.efi || {}), environment: v } }))}
            >
              <SelectTrigger data-testid="efi-env-select" className="bg-slate-950 border-slate-800 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectItem value="sandbox">Homologação (sandbox)</SelectItem>
                <SelectItem value="production">Produção</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {(data.efi?.environment || "sandbox") === "sandbox" && (
            <div
              data-testid="efi-sandbox-warning"
              className="md:col-span-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
            >
              ⚠️ Em <b>Homologação</b> a EFI simula um pagamento automático ~30s após cada cobrança. Nesse modo o
              boleto <b>não é baixado</b> no mk-auth e a mensagem de confirmação sai com rótulo de teste. Use
              <b> Produção</b> para cobranças reais.
            </div>
          )}
          <Field label="Chave PIX (recebedora)" testid="efi-pix-key">
            <Input value={data.efi?.pix_key || ""} onChange={setEfi("pix_key")} placeholder="chave cadastrada na conta EFI" className={inputCls} />
          </Field>
          <Field label="Client ID" testid="efi-client-id">
            <Input value={data.efi?.client_id || ""} onChange={setEfi("client_id")} className={inputCls} />
          </Field>
          <Field label="Client Secret" testid="efi-client-secret">
            <Input type="password" value={data.efi?.client_secret || ""} onChange={setEfi("client_secret")} placeholder="••••••••" className={inputCls} />
          </Field>
        </div>

        <div className="border-t border-slate-800 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <FileKey className="w-4 h-4 text-emerald-400" />
            <div className="text-sm text-white font-semibold">Certificado .p12</div>
            {data.efi?.has_certificate ? (
              <span data-testid="efi-cert-status" className="ml-auto text-xs text-emerald-300">
                Certificado ativo · expira {data.efi?.certificate_expires_at?.slice(0, 10)}
              </span>
            ) : (
              <span className="ml-auto text-xs text-orange-300">Nenhum certificado carregado</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Arquivo (.p12)</Label>
              <input
                data-testid="efi-cert-file"
                ref={fileRef}
                type="file"
                accept=".p12"
                className="mt-2 block w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-emerald-500 file:text-emerald-950 file:font-semibold file:cursor-pointer"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Senha do .p12 (opcional)</Label>
              <Input
                data-testid="efi-cert-password"
                type="password"
                value={certPass}
                onChange={(e) => setCertPass(e.target.value)}
                className={`${inputCls} mt-2`}
              />
            </div>
          </div>
          <div className="mt-3">
            <Button
              data-testid="efi-upload-btn"
              onClick={uploadCert}
              disabled={uploading}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <FileKey className="w-4 h-4 mr-2" /> Enviar certificado
            </Button>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-4 flex flex-wrap gap-2">
          <Button
            data-testid="efi-test-btn"
            onClick={testEfi}
            disabled={efiTesting}
            variant="outline"
            className="border-slate-700 hover:bg-slate-800 hover:text-white"
          >
            <Wifi className="w-4 h-4 mr-2" /> Testar OAuth EFI
          </Button>
          <Button
            data-testid="efi-webhook-btn"
            onClick={registerWebhook}
            variant="outline"
            className="border-slate-700 hover:bg-slate-800 hover:text-white"
          >
            <Webhook className="w-4 h-4 mr-2" /> Registrar Webhook PIX
          </Button>
          <Button
            data-testid="efi-webhook-check-btn"
            onClick={checkWebhook}
            disabled={webhookChecking}
            variant="outline"
            className="border-slate-700 hover:bg-slate-800 hover:text-white"
          >
            <Webhook className="w-4 h-4 mr-2" /> Verificar Webhook na EFI
          </Button>
          <div className="ml-auto text-xs text-slate-500 flex items-center">
            Webhook: <span className="font-mono-jb text-emerald-300 ml-1">/api/webhooks/efi/{data.id}</span>
          </div>
        </div>
        {webhookInfo && (
          <div
            data-testid="efi-webhook-status"
            className={`rounded-md border px-3 py-2 text-xs ${
              webhookInfo.ok && webhookInfo.is_ours
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : "border-amber-500/40 bg-amber-500/10 text-amber-200"
            }`}
          >
            {!webhookInfo.ok && <>Não foi possível consultar a EFI: {webhookInfo.error}</>}
            {webhookInfo.ok && webhookInfo.is_ours && <>✅ O webhook da chave PIX aponta para este sistema.</>}
            {webhookInfo.ok && !webhookInfo.is_ours && (
              <>
                ⚠️ O webhook da chave PIX na EFI aponta para <b>outro sistema</b>:{" "}
                <span className="font-mono-jb break-all">{webhookInfo.registered_url || "(nenhum)"}</span>.
                A EFI permite apenas 1 webhook por chave, então este painel <b>não recebe</b> a confirmação em tempo real;
                a baixa é feita pela <b>sincronização automática a cada 60s</b> (consulta direta à EFI). Clique em
                "Registrar Webhook PIX" apenas se quiser substituir o webhook atual.
              </>
            )}
          </div>
        )}

        <div className="border-t border-slate-800 pt-4 text-xs text-slate-500" data-testid="pix-txid-info">
          Cada cobrança PIX terá TXID no formato{" "}
          <span className="font-mono-jb text-emerald-300 mx-1">
            &lt;id do boleto&gt;&lt;timestamp&gt;&lt;random&gt;
          </span>
          — o início do TXID é o <span className="text-slate-300">ID do boleto (sis_lanc.id)</span>, facilitando identificar a quem pertence
          (26–35 caracteres, exigência EFI).
        </div>
      </Card>

      <EfiBoletoSettings />

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="openpix-card">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <KeyRound className="w-4 h-4 text-emerald-400" />
          <div className="font-outfit text-white font-semibold">OpenPix</div>
          <div className="text-xs text-slate-500 ml-auto">
            Alternativa (ou complemento) à EFI para receber os pagamentos dos seus clientes
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="AppID" testid="openpix-app-id">
            <Input
              type="password"
              value={openpix.app_id || ""}
              onChange={(e) => setOpenpix((o) => ({ ...o, app_id: e.target.value }))}
              placeholder="••••••••"
              className={inputCls}
            />
          </Field>
          <Field label="Base URL" testid="openpix-base-url">
            <Input
              value={openpix.base_url || ""}
              onChange={(e) => setOpenpix((o) => ({ ...o, base_url: e.target.value }))}
              className={inputCls}
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 pt-4">
          <Button
            data-testid="openpix-save-btn"
            onClick={saveOpenpix}
            disabled={savingOpenpix}
            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
          >
            <Save className="w-4 h-4 mr-2" /> Salvar
          </Button>
          <Button
            data-testid="openpix-webhook-btn"
            onClick={registerOpenpixWebhook}
            variant="outline"
            className="border-slate-700 hover:bg-slate-800 hover:text-white"
          >
            <Webhook className="w-4 h-4 mr-2" /> Registrar Webhook
          </Button>
          {data?.id && (
            <div className="ml-auto text-xs text-slate-500">
              Webhook: <span className="font-mono-jb text-emerald-300 ml-1">/api/webhooks/openpix/{data.id}</span>
            </div>
          )}
        </div>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="payment-split-card">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <Split className="w-4 h-4 text-emerald-400" />
            <div className="font-outfit text-white font-semibold">Divisão de recebimento (EFI x OpenPix)</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">
              {!split.efi_configured || !split.openpix_configured
                ? "Configure os dois gateways acima para dividir"
                : split.enabled ? "Divisão ativa" : "Desativada (usa EFI)"}
            </span>
            <Switch
              data-testid="split-enabled-switch"
              checked={!!split.enabled}
              disabled={!split.efi_configured || !split.openpix_configured}
              onCheckedChange={(v) => setSplit((s) => ({ ...s, enabled: v }))}
            />
          </div>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Decide qual conta recebe cada novo PIX de um <b>cliente seu</b> (envio manual/FATURA ou agendado): a EFI ou
          a OpenPix. Se só uma estiver configurada, ela é usada sempre, sem necessidade de ligar a divisão. A divisão é
          recalculada todo mês.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-950/60 border border-slate-800 rounded-md p-4">
          {["efi", "openpix"].map((p) => {
            const paid = (split.month_totals_cents?.[p] || 0) / 100;
            const open = (split.month_open_cents?.[p] || 0) / 100;
            const isTarget = split.mode === "target_value" && split.target_provider === p;
            const target = isTarget ? (split.target_value_cents || 0) / 100 : 0;
            const base = target > 0 ? target : Math.max(paid + open, 1);
            const pPaid = Math.min(100, (paid / base) * 100);
            const pOpen = Math.min(100 - pPaid, (open / base) * 100);
            const brl = (v) => `R$ ${v.toFixed(2).replace(".", ",")}`;
            return (
              <div key={p} data-testid={`split-bar-${p}`}>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">{p === "efi" ? "EFI" : "OpenPix"} — este mês</div>
                <div className="text-xl font-mono-jb text-emerald-300">{brl(paid)} <span className="text-xs text-slate-500">recebido</span></div>
                <div className="h-2 rounded-full bg-slate-800 mt-2 overflow-hidden flex">
                  <div className="bg-emerald-400" style={{ width: `${pPaid}%` }} />
                  <div className="bg-amber-400/70" style={{ width: `${pOpen}%` }} />
                </div>
                <div className="flex justify-between text-[11px] text-slate-400 mt-1">
                  <span><span className="inline-block w-2 h-2 rounded-full bg-amber-400/70 mr-1" />a receber {brl(open)}</span>
                  {isTarget && <span>meta {brl(target)} · reservado {brl(paid + open)}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500">
          "A receber" = cobranças ainda válidas (uma por boleto). Expiradas não ocupam a meta, e cada boleto fica sempre no
          mesmo banco até ser pago.
        </p>

        {split.efi_configured && split.openpix_configured && (
          <>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Como dividir</Label>
              <Select value={split.mode} onValueChange={(v) => setSplit((s) => ({ ...s, mode: v }))}>
                <SelectTrigger data-testid="split-mode-select" className="mt-1 bg-slate-950 border-slate-800 text-white h-9 max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                  <SelectItem value="percentage">Por porcentagem</SelectItem>
                  <SelectItem value="target_value">Por valor-alvo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {split.mode === "percentage" ? (
              <div className="flex items-end gap-4">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">% na EFI</Label>
                  <Input
                    data-testid="split-efi-percent-input"
                    type="number" min={0} max={100}
                    value={split.efi_percent}
                    onChange={(e) => setSplit((s) => ({ ...s, efi_percent: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-800 text-white w-24"
                  />
                </div>
                <div className="text-sm text-slate-500 pb-2">
                  = {100 - (parseInt(split.efi_percent, 10) || 0)}% na OpenPix
                </div>
              </div>
            ) : (
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Provedor principal</Label>
                  <Select value={split.target_provider} onValueChange={(v) => setSplit((s) => ({ ...s, target_provider: v }))}>
                    <SelectTrigger data-testid="split-target-provider-select" className="mt-1 bg-slate-950 border-slate-800 text-white h-9 w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                      <SelectItem value="efi">EFI</SelectItem>
                      <SelectItem value="openpix">OpenPix</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Valor-alvo (R$/mês)</Label>
                  <Input
                    data-testid="split-target-value-input"
                    value={split.target_value_raw ?? (split.target_value_cents / 100).toFixed(2).replace(".", ",")}
                    onChange={(e) => setSplit((s) => ({ ...s, target_value_raw: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-800 text-white w-32"
                  />
                </div>
                <div className="text-sm text-slate-500 pb-2">
                  Até esse valor vai pra {split.target_provider === "efi" ? "EFI" : "OpenPix"}; o restante do mês vai pra{" "}
                  {split.target_provider === "efi" ? "OpenPix" : "EFI"}.
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button data-testid="split-save-btn" onClick={saveSplit} disabled={savingSplit} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                Salvar
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <FileText className="w-4 h-4 text-emerald-400" />
          <div className="font-outfit text-white font-semibold">Imagem de Confirmação de Pagamento</div>
        </div>
        <p className="text-sm text-slate-500">
          Quando a EFI notificar o pagamento de um PIX, uma mensagem automática é enviada ao cliente no WhatsApp.
          Aqui você pode configurar a URL da imagem que aparecerá no topo dessa mensagem (opcional).
        </p>
        <Field label="URL pública da imagem (PNG/JPG)" testid="payment-confirm-image">
          <Input
            value={data.payment_confirm_image_url || ""}
            onChange={(e) => setData((d) => ({ ...d, payment_confirm_image_url: e.target.value }))}
            placeholder="https://seudominio.com/img/pago.png"
            className={inputCls}
          />
        </Field>
        {data.payment_confirm_image_url ? (
          <div className="mt-2">
            <div className="text-xs text-slate-500 mb-2">Prévia:</div>
            <img
              alt="prévia confirmação pagamento"
              src={data.payment_confirm_image_url}
              className="max-h-40 rounded-md border border-slate-800"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          </div>
        ) : null}
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="font-outfit text-white font-semibold">Agendador</div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">Ativo</span>
            <Switch
              data-testid="scheduler-switch"
              checked={!!data.scheduler_enabled}
              onCheckedChange={(v) => setData((d) => ({ ...d, scheduler_enabled: v }))}
            />
          </div>
        </div>
        <p className="text-sm text-slate-400">
          Quando ativo, a rotina diária envia as cobranças conforme os templates habilitados. Também é possível
          disparar manualmente pelo painel.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Fuso horário da empresa" testid="scheduler-timezone">
            <select
              value={data.timezone || "America/Recife"}
              onChange={(e) => { setData((d) => ({ ...d, timezone: e.target.value })); setTenantTz(e.target.value); }}
              className={inputCls}
            >
              {TIMEZONE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              {data.timezone && !TIMEZONE_OPTIONS.some(([v]) => v === data.timezone) && (
                <option value={data.timezone}>{data.timezone}</option>
              )}
            </select>
          </Field>
          <Field label="Horário de envio (hora)" testid="scheduler-hour">
            <Input
              type="number" min={0} max={23}
              value={data.scheduler_hour ?? 9}
              onChange={(e) => setData((d) => ({ ...d, scheduler_hour: e.target.value }))}
              className={inputCls}
            />
          </Field>
          <Field label="Minuto" testid="scheduler-minute">
            <Input
              type="number" min={0} max={59}
              value={data.scheduler_minute ?? 0}
              onChange={(e) => setData((d) => ({ ...d, scheduler_minute: e.target.value }))}
              className={inputCls}
            />
          </Field>
          <div className="text-xs text-slate-500 flex items-end pb-2">
            Fuso {data.timezone || "America/Recife"}. As cobranças do dia saem uma vez, às{" "}
            <span className="font-mono-jb text-emerald-300 mx-1">
              {String(data.scheduler_hour ?? 9).padStart(2, "0")}:{String(data.scheduler_minute ?? 0).padStart(2, "0")}
            </span>.
          </div>
        </div>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="font-outfit text-white font-semibold">Chatbot</div>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${data.bot_paused ? "text-amber-300" : "text-slate-500"}`}>
              {data.bot_paused ? "Bot PAUSADO" : "Bot ativo"}
            </span>
            <Switch
              data-testid="bot-paused-switch"
              checked={!!data.bot_paused}
              onCheckedChange={(v) => setData((d) => ({ ...d, bot_paused: v }))}
            />
          </div>
        </div>
        {data.bot_paused && (
          <div data-testid="bot-paused-warning" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            ⚠️ Com o bot pausado, nenhuma mensagem recebida no WhatsApp é respondida. Envios agendados e manuais continuam funcionando.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Pausa para atendimento humano (minutos)" testid="handoff-pause-minutes">
            <Input
              type="number" min={1} max={1440}
              value={data.handoff_pause_minutes ?? 10}
              onChange={(e) => setData((d) => ({ ...d, handoff_pause_minutes: e.target.value }))}
              className={inputCls}
            />
          </Field>
          <div className="text-xs text-slate-500 leading-relaxed flex items-end pb-1">
            Quando o cliente pede um atendente (ou o atendente digita <span className="font-mono-jb text-emerald-300 mx-1">#0</span> na conversa),
            o bot silencia nesta conversa por esse tempo; ao terminar, o atendimento é encerrado e a próxima mensagem reinicia o menu.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-800 pt-4">
          <Field label="Números sem bot (bypass)" testid="bypass-numbers">
            <Textarea
              data-testid="bypass-numbers-input"
              rows={5}
              placeholder={"Um número por linha, com DDD\nEx.: 11987654321"}
              value={data.bypass_numbers_text ?? (data.bypass_numbers || []).join("\n")}
              onChange={(e) => setData((d) => ({ ...d, bypass_numbers_text: e.target.value }))}
              className={`${inputCls} font-mono-jb text-sm`}
            />
          </Field>
          <div className="text-xs text-slate-500 leading-relaxed flex items-end pb-1">
            Mensagens desses números nunca passam pelo bot: sem menu, sem resposta automática e sem registro em Atendimentos.
            Use para a equipe, parceiros e fornecedores. Pode digitar com ou sem 55 e com ou sem o 9 — o sistema reconhece o mesmo número.
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-slate-800 pt-4">
          <div className="text-xs text-slate-500 leading-relaxed">
            <div className="text-slate-300 text-sm mb-1">Proteção contra loop de robôs</div>
            Se o contato também for um robô (outro chatbot ou resposta automática) e os dois ficarem se respondendo,
            o bot silencia nessa conversa por 60 minutos. A pausa aparece no Painel de Pausas e pode ser encerrada por lá.
          </div>
          <Switch
            data-testid="loop-guard-switch"
            checked={data.loop_guard_enabled !== false}
            onCheckedChange={(v) => setData((d) => ({ ...d, loop_guard_enabled: v }))}
          />
        </div>

        <div className="border-t border-slate-800 pt-4 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="text-xs text-slate-500 leading-relaxed">
              <div className="text-slate-300 text-sm mb-1">Resposta a ligações no WhatsApp</div>
              O WhatsApp não permite atender chamadas por aqui. Com isto ligado, a ligação é recusada
              automaticamente e o cliente recebe na hora uma mensagem com o menu do bot (2ª via, suporte, atendente).
              Um mesmo número só recebe a mensagem 1 vez a cada 10 minutos.
            </div>
            <Switch
              data-testid="call-reply-switch"
              checked={data.call_reply_enabled !== false}
              onCheckedChange={(v) => setData((d) => ({ ...d, call_reply_enabled: v }))}
            />
          </div>
          {data.call_reply_enabled !== false && (
            <Field label="Texto da mensagem (o menu vai logo abaixo; {nome} = primeiro nome do cliente)" testid="call-reply-text">
              <Textarea
                data-testid="call-reply-text-input"
                rows={3}
                value={data.call_reply_text ?? ""}
                onChange={(e) => setData((d) => ({ ...d, call_reply_text: e.target.value }))}
                className={inputCls}
              />
            </Field>
          )}
        </div>

        <div className="border-t border-slate-800 pt-4">
          <div className="flex items-center justify-between gap-4">
            <div className="text-xs text-slate-500 leading-relaxed">
              <div className="text-slate-300 text-sm mb-1">Avisar o cliente quando o acesso for bloqueado</div>
              O Quitaí vigia o mk-auth a cada 5 minutos. Quando um cliente passa a ficar bloqueado, ele recebe uma mensagem
              com o motivo, o vencimento, os valores e o PIX para pagar (a liberação é automática após o pagamento).
              Só avisa clientes bloqueados <b>a partir de agora</b> e respeita a janela de horário segura de envio.
              O texto é editável em <b>Templates → Bloqueio de acesso</b>.
            </div>
            <Switch
              data-testid="block-notice-switch"
              checked={data.block_notice_enabled !== false}
              onCheckedChange={(v) => setData((d) => ({ ...d, block_notice_enabled: v }))}
            />
          </div>
        </div>

        <HumanHoursEditor
          value={data.human_hours}
          onChange={(v) => setData((d) => ({ ...d, human_hours: v }))}
        />

        <div className="border-t border-slate-800 pt-4">
          <div className="text-slate-300 text-sm mb-1">Multa e juros (cobrança avulsa)</div>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Aplicados automaticamente pelo banco (EFI/OpenPix) nas cobranças avulsas em caso de atraso. Deixe em 0
            para desativar. O desconto por antecipação é escolhido na hora de cadastrar cada cobrança, não aqui.
            Exige CPF e endereço completos no cadastro do cliente no mk-auth.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Multa (%)</label>
              <Input
                data-testid="avulsa-multa-input"
                type="number" step="0.01" min="0" max="100"
                value={data.avulsa_multa_pct ?? 0}
                onChange={(e) => setData((d) => ({ ...d, avulsa_multa_pct: parseFloat(e.target.value) || 0 }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Juros ao mês (%)</label>
              <Input
                data-testid="avulsa-juros-input"
                type="number" step="0.01" min="0" max="100"
                value={data.avulsa_juros_pct ?? 0}
                onChange={(e) => setData((d) => ({ ...d, avulsa_juros_pct: parseFloat(e.target.value) || 0 }))}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <QuickRepliesEditor
          value={data.quick_replies || []}
          onChange={(list) => setData((d) => ({ ...d, quick_replies: list }))}
        />
      </Card>

      <div className="flex justify-end">
        <Button
          data-testid="settings-save-btn"
          onClick={save}
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
        >
          <Save className="w-4 h-4 mr-2" /> Salvar Configurações
        </Button>
      </div>
    </div>
  );
}

function parseBypassNumbers(text) {
  return String(text || "")
    .split(/[\n,;]+/)
    .map((n) => n.replace(/\D/g, ""))
    .filter((n) => n.length >= 8);
}

const inputCls =
  "bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500";

function Field({ label, testid, children }) {
  return (
    <div className="space-y-2" data-testid={`field-${testid}`}>
      <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">{label}</Label>
      {children}
    </div>
  );
}
